import './UploadedFiles.scss'

import { List } from '@chakra-ui/react'
import { CSSProperties, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import SummarizeIcon from '@/components/assets/SummarizeIcon'
import { Box } from '@/components/ui/box'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Text } from '@/components/ui/typography'
import useColorModeValue from '@/hooks/useColorModeValue'
import useCustomMediaQuery from '@/hooks/useCustomMediaQuery'
import Search from '@/pages/components/search'
import { toggleUploadMobileDrawer } from '@/store/gigiChat/ChatAtom'
import { IUploadFiles } from '@/types/documents/File'
import { IDocument } from '@/types/gigiChat/ConversationSettings'

import FileItem from './components/FileItem'

const UploadedFiles = ({ files, onItemClick, onSelectAll, onDelete, onSumarize, loadingDeleteIndex }: IUploadFiles) => {
    const { t } = useTranslation()

    const [isAllSelected, setIsAllSelected] = useState<boolean>(false)
    const [isAtLeastOneSelected, setIsAtLeastOneSelected] = useState<boolean>(false)
    const [isDisabled, setIsDisabled] = useState<boolean>(false)
    const [isMobile] = useCustomMediaQuery()
    const isIndeterminate = files.some((item) => item.selected) && !isAllSelected

    useEffect(() => {
        if (files.every((item) => item.selected === true)) {
            setIsAllSelected(true)
        } else if (isAllSelected) {
            setIsAllSelected(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [files])

    useEffect(() => {
        if (files.length && files.every((item) => item.ready === false)) {
            setIsDisabled(true)
        } else if (isDisabled) {
            setIsDisabled(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [files])

    useEffect(() => {
        if (files.length && files.some((item) => item.selected === true)) {
            setIsAtLeastOneSelected(true)
        } else {
            setIsAtLeastOneSelected(false)
        }
    }, [files])

    const onSelectAllClicked = () => {
        setIsAllSelected(!isAllSelected)
        onSelectAll(!isAllSelected)
    }

    const generateFileList = (): React.ReactNode => {
        return files.map((item: IDocument, index: number) => {
            return (
                <FileItem
                    key={item.name + index}
                    name={item.name}
                    index={index}
                    isSelected={isAllSelected || item.selected}
                    onItemClick={onItemClick}
                    onDelete={onDelete}
                    isDeleteDisabled={loadingDeleteIndex === index}
                    loadingDeleteIndex={loadingDeleteIndex}
                    isDisabled={item.ready}
                    onSumarize={onSumarize}
                />
            )
        })
    }

    const disabledItemStyle: CSSProperties = {
        opacity: '0.4',
        cursor: 'not-allowed',
        pointerEvents: 'none',
    }

    const disabledStyle = isDisabled ? disabledItemStyle : undefined
    //search should be hidden if all files are in pending state
    const shouldHideSearch = !!files.length && files.every((item) => !item.ready)

    const onSummarizeAll = () => {
        let fileNames: string = ''
        files.forEach((item) => {
            if (item.selected) {
                const separator = fileNames ? ',' : ''
                fileNames += separator + item.name
            }
        })
        onSumarize(`${t('documents.summarize')} ${fileNames}`)
        if (isMobile) {
            toggleUploadMobileDrawer()
        }
    }

    return (
        <Box w="99%" display="inline-block" overflow="auto" minH="50px">
            <Text
                fontSize="12px"
                color={useColorModeValue('rgba(84, 79, 64, 0.7)', 'rgba(255, 255, 255, 0.7)')}
                letterSpacing="0.5px"
                marginTop="16px"
                marginBottom="16px"
            >
                {t('documents.uploadedFiles')}
            </Text>
            <Box display="flex" alignItems="center" style={disabledStyle} hidden={shouldHideSearch} mb="16px">
                <Checkbox
                    variant="gsk"
                    className="uploadedFiles"
                    alignItems="top"
                    onCheckedChange={onSelectAllClicked}
                    checked={isIndeterminate ? 'indeterminate' : isAllSelected}
                    width="24px"
                    mt="-6px"
                    mr="15px"
                />

                <Search location="searchDocs" />
                <Button variant="primary" onClick={onSummarizeAll} ml="16px" h="32px" disabled={!isAtLeastOneSelected} display="none">
                    <SummarizeIcon color="var(--chakra-colors-white)" style={{ height: '22px' }} />
                    <Text
                        ml="8px"
                        fontSize="12px"
                        fontFamily="GSKPrecision-Bold"
                        fontWeight="bold"
                        textTransform="uppercase"
                        letterSpacing="1.5px"
                        lineHeight="16px"
                    >
                        {t('documents.summarizeButtonTitle')}
                    </Text>
                </Button>
            </Box>

            <Box>
                <List.Root variant="gigi">{generateFileList()}</List.Root>
            </Box>
        </Box>
    )
}

export default UploadedFiles
